import recommendClient from "@algolia/recommend";
import React, { Fragment, useContext, useEffect, useRef } from "react";
import { decodeFilters, isValidArray } from "./common";
import {
  Configure,
  useClearRefinements,
  useInstantSearch,
  useRefinementList,
  useStats,
  useTrendingItems,
} from "react-instantsearch";
import { useAuth } from "../../Context/AuthContext";
import { useLocation } from "react-router-dom";
import algoliasearch from "algoliasearch";
import { axiosInstance } from "../../axios/axiosInstance";
import { toRelativeUrl } from "../../utils/urlHelpers";
import { handleStockStatus } from "../../utils/stockStatus";

export const searchClient = algoliasearch(
  `${process.env.REACT_APP_ALGOLIA_APP_ID}`,
  `${process.env.REACT_APP_ALGOLIA_SEARCH_KEY}`,
);

export const recommend = recommendClient(
  `${process.env.REACT_APP_ALGOLIA_APP_ID}`,
  `${process.env.REACT_APP_ALGOLIA_SEARCH_KEY}`,
);

// Ceiling for facet values returned per attribute. MUST be >= the `limit` used by
// CustomHierarchicalMenu, otherwise the category tree gets truncated.
//
// Why: connectHierarchicalMenu raises maxValuesPerFacet via Math.max(current, limit), but
// <Configure> OVERWRITES it (mergeSearchParameters -> setQueryParameters). Whichever applies
// last wins. On first load the menu wins; the moment `distinct` flips, Configure re-applies and
// its value sticks. With a lower number here, categories silently disappear after the first
// filter toggle and never come back. 1000 is Algolia's effective cap.
export const MAX_FACET_VALUES = 1000;

export const newproductindexName = process.env.REACT_APP_ALGOLIA_NEWPRODUCT_INDEX_NAME;
export const indexName = process.env.REACT_APP_ALGOLIA_INDEX_NAME;
export const suggestionIndexName = process.env.REACT_APP_ALGOLIA_SUGGESTION_INDEX;

export const searchIndex = async (indexName, params = {}) => {
  const response = await searchClient.search([
    {
      indexName,
      params,
    },
  ]);
  return response.results[0];
};

export async function getFacetsOnly() {
  const data = await searchClient.search([
    {
      indexName: indexName,
      params: {
        query: "",
        facets: ["facets"],
        hitsPerPage: 0,
        maxValuesPerFacet: 300,
        sortFacetValuesBy: "count",
      },
    },
  ]);
  const response = data.results[0];
  const filtered = Object.keys(response.facets.facets).filter((item) => item != "category");
  const facetList = filtered.map((item) => {
    if (item != "category") {
      return "prodSpecs." + item;
    } else return item;
  });
  return facetList;
}

export const SORTBY_OPTION = [
  {
    value: `${indexName}`,
    label: "Relevance",
    id: "Relevance",
  },
  {
    value: `${indexName}_price_asc_wes`,
    label: "Low to High",
    id: "ltoh",
  },
  {
    value: `${indexName}_price_H_L_wes`,
    label: "High to Low",
    id: "htol",
  },
  {
    value: `${indexName}_price_desc`,
    label: "Latest",
    id: "Latest",
  },
];

export const LATEST_SORTBY_VALUE = SORTBY_OPTION.find((item) => item.id === "Latest").value;

export const SORTBY_STORAGE_KEY = "wesAlgoliaSortBy";

export function getStoredSortBy() {
  try {
    const stored = window.localStorage.getItem(SORTBY_STORAGE_KEY);
    return SORTBY_OPTION.some((item) => item.value === stored) ? stored : null;
  } catch {
    return null;
  }
}

export function setStoredSortBy(value) {
  try {
    if (SORTBY_OPTION.some((item) => item.value === value)) {
      window.localStorage.setItem(SORTBY_STORAGE_KEY, value);
    }
  } catch {}
}

export function getInitialSortByUiState(rootIndexName) {
  const stored = getStoredSortBy();
  if (!stored || stored === rootIndexName) return undefined;
  return { [rootIndexName]: { sortBy: stored } };
}

export const UseCaptureFacets = ({ setFacets }) => {
  const { items, refine } = useRefinementList({
    attribute: "facets",
    limit: 100,
    sortBy: ["count:desc"],
  });
  useEffect(() => {
    if (setFacets) {
      setFacets(items.filter((item) => item.label.toLowerCase() !== "manufacturer").map((item) => item.label));
    }
  }, [items, setFacets]);

  return null;
};

export const UseSearchStats = () => {
  const { nbHits } = useStats();
  return <>{nbHits} Products Found</>;
};

export const ClearAllRefinement = () => {
  const { canRefine, refine } = useClearRefinements({ excludedAttributes: ["categories.lvl0", "query"] });
  return (
    <Fragment>
      {canRefine && (
        <button className="clear-filters-btn" onClick={() => refine()}>
          Clear Filters
        </button>
      )}
    </Fragment>
  );
};

export function ResetFiltersOnQueryChange({ query }) {
  const { setUiState } = useInstantSearch();
  const prevQuery = useRef(query);

  useEffect(() => {
    if (prevQuery.current !== query) {
      setUiState((uiState) => {
        const indexId = Object.keys(uiState)[0];
        // Drop `page` as well as the refinements. <Configure query> does NOT reset paging
        // (connectConfigure has no page handling), and DynamicLoader auto-advances pages on a
        // timer - so without this a new query is requested at whatever page the auto-loader had
        // reached, skipping its first N pages and returning results that then get appended to
        // the previous query's.
        const nextIndexState = { ...(uiState[indexId] ?? {}), refinementList: {} };
        delete nextIndexState.page;
        return { ...uiState, [indexId]: nextIndexState };
      });
      prevQuery.current = query;
    }
  }, [query, setUiState]);

  return null;
}
export function ApplyDisjunctiveFiltersFromUrl() {
  const { setUiState } = useInstantSearch();
  const { search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    var filters = params.getAll("f");
    var decodeFilter = decodeFilters(filters[0]);
    var refinements = {};
    Object.keys(decodeFilter).map((key) => {
      refinements[key] = decodeFilter[key];
    });
    setUiState((uiState) => {
      const indexId = Object.keys(uiState)[0];
      return {
        ...uiState,
        [indexId]: {
          ...uiState[indexId],
          refinementList: {
            ...uiState[indexId]?.refinementList,
            ...refinements,
          },
        },
      };
    });
  }, [setUiState]);

  return null;
}

export function hasActiveRefinements(indexUiState) {
  const refinementList = indexUiState?.refinementList;
  if (!refinementList) return false;
  return Object.values(refinementList).some((values) => Array.isArray(values) && values.length > 0);
}

// Configure for "grouped result" pages (brand / brand-model): while browsing (no facet filter),
// distinct=true so the index collapses to one record per family (attributeForDistinct=familyId).
// As soon as any facet filter is applied, distinct=false so every matching record returns and the
// grid does the manual family grouping (same behaviour as the category page). `filters` is the
// page's static scope string (e.g. the manufacturer / compatible-model filter).
export function GroupedConfigure({ filters }) {
  const { indexUiState } = useInstantSearch();
  const distinct = hasActiveRefinements(indexUiState) ? false : true;
  return <Configure hitsPerPage={50} maxValuesPerFacet={MAX_FACET_VALUES} distinct={distinct} filters={filters} />;
}

// Fires onReady() exactly once — the first time a search SETTLES after at least one in-flight cycle.
// Used to dismiss a page loader when a page's initial results are ready (mount it inside InstantSearch).
export function FirstResultsGate({ onReady }) {
  const { status } = useInstantSearch();
  const seenLoading = useRef(false);
  const fired = useRef(false);
  useEffect(() => {
    if (status === "loading" || status === "stalled") seenLoading.current = true;
    if (!fired.current && seenLoading.current && status === "idle") {
      fired.current = true;
      if (onReady) onReady();
    }
  }, [status, onReady]);
  return null;
}

export function ApplyDisjunctiveFilters({ category }) {
  const { setUiState } = useInstantSearch();

  useEffect(() => {
    if (category) {
      setUiState((prev) => ({
        ...prev,
        hierarchicalMenu: {
          ...prev.hierarchicalMenu,
          "categories.lvl0": [category],
        },
      }));
    }
  }, [category, setUiState]);

  return null;
}

export function getUniqueFamiliesByLowestPriceOld(products, priceCode) {
  try {
    const lowestByFamily = new Map();
    const familyCount = new Map();
    const familyGroup = new Map();
    const addedFamilies = new Set();
    const result = [];
    priceCode = priceCode ?? 3;

    if (!isValidArray(products)) return [];

    products.forEach((product) => {
      if (!familyGroup.has(product.familyId)) {
        familyGroup.set(product.familyId, []);
      }
      familyGroup.get(product.familyId).push(product);
    });

    familyGroup.forEach((group, familyId) => {
      try {
        let lowestProduct = null;
        let hidePrice = false;
        if (isValidArray(group) && group.length > 1) {
          const nonSubfamily = group.filter((p) => p.isSubFamily == false);

          if (isValidArray(nonSubfamily) && nonSubfamily.length > 0) {
            nonSubfamily.sort((a, b) => a.prodExtPri3 - b.prodExtPri3);
            lowestProduct = { ...nonSubfamily[0] };
            hidePrice = false;
          } else {
            group.sort((a, b) => a.prodExtPri3 - b.prodExtPri3);
            lowestProduct = { ...group[0] };
            hidePrice = true;
          }
          lowestProduct.isfamily = true;
          lowestByFamily.set(familyId, lowestProduct);
        } else {
          lowestProduct = { ...group[0] };
          lowestProduct.isfamily = lowestProduct.isParentProductEmpty ?? false;
          hidePrice = false;
          lowestByFamily.set(familyId, lowestProduct);
        }
        result.push({
          ...lowestProduct,
          // Stable identity so React reuses cards across filter/query/view changes (no remount/re-fade).
          key: String(lowestProduct.familyId ?? lowestProduct.sku ?? lowestProduct.productId ?? lowestProduct.objectID),
          description: "",
          desc: "",
          prodImage: "",
          link: "",
          hidePrice,
          isBuyOption: false,
          price: lowestProduct[`prodExtPri${priceCode}`] ?? lowestProduct.prodExtPri3,
          productCode: lowestProduct.sku,
          isUnavailable: false,
        });
      } catch (error) {
        console.error("Error grouping family:", error, familyId);
      }
    });
    return result;
  } catch (error) {
    console.error("Error in getUniqueFamiliesByLowestPriceOld:", error);
    return [];
  }
}

// --- helpers ---

function slugifySegment(s) {
  return String(s)
    .toLowerCase()
    .replace(/&/g, " ") // drop ampersands ("& Valves" -> " Valves")
    .replace(/[^a-z0-9]+/g, "-") // anything non-alphanumeric -> dash
    .replace(/^-+|-+$/g, "") // trim leading/trailing dashes
    .replace(/-+/g, "-"); // collapse repeats
}

// accepts an array of level names (["Electronic Components", "Vacuum Tubes & Valves"] - what
// hierarchicalMenu uiState holds), OR a URL path ("/electronic-components/..."), OR a
// ">"-delimited filter string ("Electronic Components>Vacuum Tubes & Valves")
function toCategorySlugs(input) {
  if (!input) return [];
  debugger;
  if (Array.isArray(input)) return input.map(slugifySegment).filter(Boolean);
  const sep = String(input).includes(">") ? ">" : "/";
  return String(input).split(sep).map(slugifySegment).filter(Boolean);
}

function familyMatchesCategory(family, categorySlugs) {
  if (!isValidArray(categorySlugs)) return true; // no category filter -> matches all
  const paths = family?.categoryPath ?? [];
  return paths.some((path) => {
    const pathSlugs = toCategorySlugs(path);
    // prefix match: every current-category slug must line up from the start
    return categorySlugs.every((slug, i) => pathSlugs[i] === slug);
  });
}

// pick the family in details[] that belongs to the current category page
function findMatchingFamily(product, categorySlugs) {
  try {
    const details = product?.details ?? [];
    if (!isValidArray(details)) return null;

    // root families only (parentFamilyId == 0); fall back to all details if none
    const roots = details.filter((d) => d && String(d.parentFamilyId ?? "0") === "0");
    const candidates = isValidArray(roots) ? roots : details;

    // ONLY ONE family -> nothing to disambiguate, skip slug matching entirely
    if (candidates.length === 1) return candidates[0];

    // 2+ root families -> figure out which one belongs to this category page
    const byTopLevel = candidates.find((d) => d && String(d.familyId) === String(product?.familyId)) ?? candidates[0];

    if (!isValidArray(categorySlugs)) return byTopLevel;

    // Most specific first, then progressively shallower.
    //
    // familyMatchesCategory is an all-or-nothing prefix match, so asking for every level at once
    // would match NOTHING whenever categoryPath does not carry the full root-down path - and each
    // product would silently fall back to byTopLevel, i.e. the wrong family and price. Walking the
    // depth down means a deep category page narrows as far as the data actually supports, and the
    // worst case is depth 1, which is exactly the root-only behaviour this had before.
    for (let depth = categorySlugs.length; depth > 0; depth--) {
      const matched = candidates.find((d) => familyMatchesCategory(d, categorySlugs.slice(0, depth)));
      if (matched) return matched;
    }
    return byTopLevel;
  } catch (error) {
    console.error("Error in findMatchingFamily:", error, product);
    return null;
  }
}

let cachedPriceCode;
let priceCodeRequest = null;

export const loadPriceCode = () => {
  if (!priceCodeRequest) {
    priceCodeRequest = axiosInstance
      .post("Authentication", { LoginName: "", token: "" })
      .then((response) => {
        const code = response?.data?.data?.priceCode;
        if (code !== undefined && code !== null) cachedPriceCode = code;
        return cachedPriceCode;
      })
      .catch((error) => {
        console.error("Error in loadPriceCode:", error);
        return cachedPriceCode;
      })
      .finally(() => {
        priceCodeRequest = null;
      });
  }
  return priceCodeRequest;
};

export function getUniqueFamiliesByLowestPrice(products, priceCode, currentPath, options = {}) {
  try {
    const { familyByProductCount = false } = options;
    const lowestByFamily = new Map();
    const familyGroup = new Map();
    const result = [];
    priceCode = priceCode ?? cachedPriceCode;
    console.log("priceCode:", priceCode);
    if (!isValidArray(products)) return [];

    const categorySlugs = toCategorySlugs(currentPath);
    const normalized = products
      .map((product) => {
        try {
          const fam = findMatchingFamily(product, categorySlugs);
          if (!fam) return null;
          return {
            ...product,
            familyId: fam.familyId,
            isSubFamily: fam.isSubFamily,
            isParentProductEmpty: fam.isParentProductEmpty,
            familyName: fam.familyName,
            categoryPath: fam.categoryPath,
            familyData: fam,
          };
        } catch (error) {
          console.error("Error normalizing product:", error, product);
          return null;
        }
      })
      .filter(Boolean);

    normalized.forEach((product) => {
      if (!familyGroup.has(product.familyId)) {
        familyGroup.set(product.familyId, []);
      }
      familyGroup.get(product.familyId).push(product);
    });

    familyGroup.forEach((group, familyId) => {
      try {
        let lowestProduct = null;
        let hidePrice = false;
        if (isValidArray(group) && group.length > 1) {
          const nonSubfamily = group.filter((p) => p.isSubFamily == false);

          if (isValidArray(nonSubfamily) && nonSubfamily.length > 0) {
            nonSubfamily.sort((a, b) => a.prodExtPri3 - b.prodExtPri3);
            lowestProduct = { ...nonSubfamily[0] };
            hidePrice = false;
          } else {
            group.sort((a, b) => a.prodExtPri3 - b.prodExtPri3);
            lowestProduct = { ...group[0] };
            hidePrice = true;
          }
          lowestProduct.isfamily = true;
          lowestByFamily.set(familyId, lowestProduct);
        } else {
          lowestProduct = { ...group[0] };
          lowestProduct.isfamily = lowestProduct.isParentProductEmpty ?? false;
          hidePrice = false;
          lowestByFamily.set(familyId, lowestProduct);
        }
        if (familyByProductCount) {
          const productCount = Number(lowestProduct.familyData?.productCount ?? lowestProduct.productCount ?? 0);
          lowestProduct.isfamily = productCount > 1;
          lowestByFamily.set(familyId, lowestProduct);
        }

        if (lowestProduct.hybridFlag && lowestProduct.hybridFlag === "1") {
          let prodCount = Number(lowestProduct.familyData?.productCount ?? lowestProduct.productCount ?? 0);
          lowestProduct.isfamily = prodCount > 1 ? true : false;
          lowestProduct.wesFamilyUrl = lowestProduct.wesProductUrl;
        }

        result.push({
          ...lowestProduct,
          // Stable identity so React reuses cards across filter/query/view changes (no remount/re-fade).
          key: String(lowestProduct.familyId ?? lowestProduct.sku ?? lowestProduct.productId ?? lowestProduct.objectID),
          description: "",
          desc: "",
          prodImage: "",
          link: "",
          hidePrice,
          isBuyOption: false,
          price: lowestProduct[`prodExtPri${priceCode}`] ?? lowestProduct.prodExtPri3,
          productCode: lowestProduct.sku,
          isUnavailable: false,
        });
      } catch (error) {
        console.error("Error grouping family:", error, familyId);
      }
    });
    return result;
  } catch (error) {
    console.error("Error in getUniqueFamiliesByLowestPrice:", error);
    return [];
  }
}

export function getSearchFamiliesByLowestPrice(products, priceCode) {
  try {
    const familyGroup = new Map();
    const result = [];
    priceCode = priceCode ?? cachedPriceCode;
    if (!isValidArray(products)) return [];

    products.forEach((product) => {
      const familyId = product.familyId ?? product.objectID;
      if (!familyGroup.has(familyId)) {
        familyGroup.set(familyId, []);
      }
      familyGroup.get(familyId).push(product);
    });

    familyGroup.forEach((group, familyId) => {
      try {
        let parentGroup = group.filter((p) => p.showMinPrice == 1);
        parentGroup = parentGroup.length > 0 ? parentGroup : group;
        const sorted = [...parentGroup].sort((a, b) => a.prodExtPri3 - b.prodExtPri3);
        console.log("[getSearchFamiliesByLowestPrice] sorted:", sorted);
        const family = sorted[0].details.find((p) => p.familyId == familyId);

        const lowestProduct = { ...sorted[0], isfamily: group.length > 1 && family.productCount > 1 };
        result.push({
          ...lowestProduct,
          key: String(familyId),
          description: "",
          desc: "",
          prodImage: "",
          link: lowestProduct.hybridFlag == "1" ? lowestProduct.wesProductUrl : lowestProduct.wesFamilyUrl,
          hidePrice: false,
          isBuyOption: false,
          price: lowestProduct[`prodExtPri${priceCode}`] ?? lowestProduct.prodExtPri3,
          productCode: lowestProduct.sku,
          isUnavailable: false,
        });
      } catch (error) {
        console.error("Error grouping search family:", error, familyId);
      }
    });
    return result;
  } catch (error) {
    console.error("Error in getSearchFamiliesByLowestPrice:", error);
    return [];
  }
}
export const contructProductData = (data, catFilter) => {
  try {
    if (!data) return null;
    const fid = data.familyId;
    const hit = data;
    const details = hit.details || [];
    const isfamily = hit.isfamily;
    let familyDetails = hit.familyData;
    if (!familyDetails) familyDetails = details.find((detail) => detail && detail.familyId == fid);
    // guard against a missing family record so the property reads below never throw
    familyDetails = familyDetails || {};

    const fName = familyDetails && familyDetails.familyName ? familyDetails.familyName : hit.productDescriptions;

    let familyUrl = hit.wesFamilyUrl ? hit.wesFamilyUrl : "";
    const descriptions = familyDetails.descriptions ? familyDetails.descriptions : hit.productDescriptions;
    const desc = String(descriptions).replaceAll("<B>", "").split("<ul>")[0];
    let showMinPrice = true;
    try {
      if (hit.showMinPrice == 0 && isfamily == true) {
        showMinPrice = false;
      }
    } catch {
      showMinPrice = true;
    }

    let imageUrl = hit.imageUrl1 ? hit.imageUrl1 : "https://cdn.wes.com.au//prodimages" + familyDetails.familyImage1;
    if (isfamily == false) {
      const productQuery = String(hit.wesProductUrl ?? "").split(/(?=\?)/)[1] ?? "";
      familyUrl = hit.familyData?.wesFamilyUrl ? hit.familyData.wesFamilyUrl + productQuery : hit.wesProductUrl;
    } else {
      imageUrl = "https://cdn.wes.com.au//prodimages" + familyDetails.familyImage1;
    }
    //hide for hybrid page
    // if (isfamily === true) {
    //   familyUrl = hit.hybridFlag == "1" ? familyUrl + "?sku=" + hit.sku : familyUrl;
    // }
    familyUrl = toRelativeUrl(familyUrl);
    const stockStatus = handleStockStatus(hit);
    return {
      ...data,
      description: fName,
      desc: desc,
      prodImage: imageUrl,
      stockStatus: stockStatus,
      link: familyUrl,
      isBuyOption: false,
      productCode: data.sku,
      isUnavailable: stockStatus?.isUnavailable ?? false,
      isSubstitute: stockStatus?.isSubstitute ?? false,
      isDiscontinued: stockStatus?.isDiscontinued ?? false,
      eBayBlock: hit.eBayBlock == "True" ? true : false,
    };
  } catch (error) {
    console.error("Error in contructProductData:", error, data?.sku);
    return null;
  }
};

export const fetchProducts = async (type) => {
  try {
    //console.log("fetchProducts called with type:", `${process.env.REACT_APP_ALGOLIA_NEWPRODUCT_INDEX_NAME}`);
    //let index = null;
    //let filter = "";
    //if (type === "NEW_PRODUCTS") {
    //  index = newproductindexName;
    //  filter = "messageToDisplay:In Stock";
    //} else if (type === "CLEARANCE_PRODUCTS") {
    //  index = newproductindexName;
    //  filter = "categories.lvl0:Clearance Products";
    //} else if (type === "SPECIAL_PRODUCTS") {
    //  index = indexName;
    //  filter = "IsProductSpecial:1";
    //}

    //const result = await searchClient.search([
    //  {
    //    indexName: index,
    //    params: {
    //      query: "",
    //      hitsPerPage: 100,
    //      facets: ["category"],
    //      facetFilters: [filter],
    //    },
    //  },
    //]);
    //const data = result.results[0];
    //  let groupedItems = getUniqueFamiliesByLowestPrice(data.hits);
    //  console.log("Fetched family:", groupedItems);
    //  return groupedItems;

    //// Convert data to text (JSON format)
    //const fileContent = JSON.stringify(data, null, 2);

    //// File name based on type
    //const fileName = `${type}_products.txt`;

    //// Create blob
    //const blob = new Blob([fileContent], { type: "text/plain" });

    //// Create download link
    //const link = document.createElement("a");
    //link.href = URL.createObjectURL(blob);
    //link.download = fileName;

    //// Trigger download
    //document.body.appendChild(link);
    //link.click();
    //document.body.removeChild(link);

    const data = {
      fileName: type,
    };

    const response = await axiosInstance.post("ProductNew/getProductFileText", data);

    if (response.data !== "") {
      const parsed = JSON.parse(response.data);
      await loadPriceCode();
      const groupedItems = getUniqueFamiliesByLowestPrice(parsed);
      return groupedItems; // ✅ return after data ready
    }

    return [];
  } catch (error) {
    console.error("Error fetching products:", error);
    return [];
  }
};

export const fetchTrendingProducts = async (type) => {
  const result = await recommend.getTrendingItems([
    {
      indexName: indexName,
      maxRecommendations: 30,
    },
  ]);

  const hits = result.results?.[0]?.hits || [];
  await loadPriceCode();
  const groupedItems = getUniqueFamiliesByLowestPrice(hits);
  return groupedItems;
};

export const fetchBoughtTogetherProducts = async (productId) => {
  const result = await recommend.getRecommendations([
    {
      indexName: indexName,
      model: "bought-together",
      objectID: productId,
      maxRecommendations: 30,
    },
  ]);

  const hits = result.results?.[0]?.hits || [];
  await loadPriceCode();
  const groupedItems = getUniqueFamiliesByLowestPrice(hits);
  return groupedItems;
};

export const fetchRelatesProducts = async (productId) => {
  const result = await recommend.getRecommendations([
    {
      indexName: indexName,
      model: "related-products",
      objectID: productId,
      maxRecommendations: 30,
    },
  ]);

  const hits = result.results?.[0]?.hits || [];
  await loadPriceCode();
  const groupedItems = getUniqueFamiliesByLowestPrice(hits);
  return groupedItems;
};
